interface Agreement {
  allAgree: boolean;
  terms: boolean;
  privacy: boolean;
  age: boolean;
  privacyDelegate: boolean;
  location?: boolean;
  marketing?: boolean;
}

interface AgreementSectionProps {
  agreement: Agreement;
  onAllAgree: () => void;
  onAgreementChange: (field: keyof Agreement) => void;
  onTermsView: (type: 'terms' | 'privacy' | 'delegate' | 'location') => void;
  className?: string;
}

export function AgreementSection({ 
  agreement, 
  onAllAgree, 
  onAgreementChange, 
  onTermsView, 
  className = '' 
}: AgreementSectionProps) {
  return (
    <div className={`space-y-6 ${className}`}>
      <h3 className="text-[20px] font-bold text-[#222222]">약관 동의</h3>
      
      {/* 전체 동의 */}
      <div className="bg-[#F5F5F5] rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="allAgree"
              checked={agreement.allAgree}
              onChange={onAllAgree}
              className="w-5 h-5 text-[#51C854] border-2 border-[#CCCCCC] rounded focus:ring-0"
            />
            <label htmlFor="allAgree" className="text-[18px] font-semibold text-[#222222]">
              약관 전체 동의
            </label>
          </div>
        </div>
        <p className="text-[14px] text-[#666666] mt-2 ml-8">
          필수 및 선택 항목에 모두 동의합니다.
        </p>
      </div>

      {/* 개별 약관 */}
      <div className="space-y-4">
        {/* 필수 약관들 */}
        <div className="space-y-3">
          <h4 className="text-[16px] font-semibold text-[#222222] border-b pb-2">필수 동의 항목</h4>
          
          {/* 서비스 이용약관 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={agreement.terms}
                onChange={() => onAgreementChange('terms')}
                className="w-4 h-4 text-[#51C854] border-2 border-[#CCCCCC] rounded focus:ring-0"
              />
              <label htmlFor="terms" className="text-[16px] text-[#222222]">
                [필수] 서비스 이용약관 동의
              </label>
            </div>
            <button
              onClick={() => onTermsView('terms')}
              className="text-[14px] text-[#666666] underline hover:text-[#222222]"
            >
              보기
            </button>
          </div>

          {/* 개인정보 수집 이용 동의 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="privacy"
                checked={agreement.privacy}
                onChange={() => onAgreementChange('privacy')}
                className="w-4 h-4 text-[#51C854] border-2 border-[#CCCCCC] rounded focus:ring-0"
              />
              <label htmlFor="privacy" className="text-[16px] text-[#222222]">
                [필수] 개인정보 수집 이용 동의
              </label>
            </div>
            <button
              onClick={() => onTermsView('privacy')}
              className="text-[14px] text-[#666666] underline hover:text-[#222222]"
            >
              보기
            </button>
          </div>

          {/* 만 14세 이상 동의 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="age"
                checked={agreement.age}
                onChange={() => onAgreementChange('age')}
                className="w-4 h-4 text-[#51C854] border-2 border-[#CCCCCC] rounded focus:ring-0"
              />
              <label htmlFor="age" className="text-[16px] text-[#222222]">
                [필수] 만 14세 이상입니다.
              </label>
            </div>
          </div>

          {/* 개인정보 처리방침 동의 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="privacyDelegate"
                checked={agreement.privacyDelegate}
                onChange={() => onAgreementChange('privacyDelegate')}
                className="w-4 h-4 text-[#51C854] border-2 border-[#CCCCCC] rounded focus:ring-0"
              />
              <label htmlFor="privacyDelegate" className="text-[16px] text-[#222222]">
                [필수] 개인정보 처리방침 동의
              </label>
            </div>
            <button
              onClick={() => onTermsView('delegate')}
              className="text-[14px] text-[#666666] underline hover:text-[#222222]"
            >
              보기
            </button>
          </div>

        </div>

        {/* 선택 약관들 */}
        <div className="space-y-3 pt-4 border-t">
          <h4 className="text-[16px] font-semibold text-[#222222]">선택 동의 항목</h4>
          
          {/* 위치기반서비스 이용약관 동의 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="location"
                checked={agreement.location || false}
                onChange={() => onAgreementChange('location')}
                className="w-4 h-4 text-[#51C854] border-2 border-[#CCCCCC] rounded focus:ring-0"
              />
              <label htmlFor="location" className="text-[16px] text-[#222222]">
                [선택] 위치기반서비스 이용약관 동의
              </label>
            </div>
            <button
              onClick={() => onTermsView('location')}
              className="text-[14px] text-[#666666] underline hover:text-[#222222]"
            >
              보기
            </button>
          </div>


          {/* 마케팅 정보 수신 동의 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="marketing"
                checked={agreement.marketing || false}
                onChange={() => onAgreementChange('marketing')}
                className="w-4 h-4 text-[#51C854] border-2 border-[#CCCCCC] rounded focus:ring-0"
              />
              <label htmlFor="marketing" className="text-[16px] text-[#222222]">
                [선택] 마케팅 정보 수신 동의
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}